import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { useEffect, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import './i18n';
import ShortCodeRedirect from './components/ShortCodeRedirect';

// Route pages are lazy-loaded so each compiles to its own chunk instead of
// bloating the initial bundle. The <Suspense> boundary around <Routes> covers
// the brief async chunk load when navigating to a route.
const FeaturesPage = lazy(() => import('./pages/Features'));
const AboutPage = lazy(() => import('./pages/About'));
const BookDemoPage = lazy(() => import('./pages/BookDemo'));
const PressPage = lazy(() => import('./pages/Press'));
const LearnMorePage = lazy(() => import('./pages/LearnMore'));
const SecurityPage = lazy(() => import('./pages/Security'));
const EnterprisePage = lazy(() => import('./pages/Enterprise'));
const DocumentationPage = lazy(() => import('./pages/Documentation'));
const HelpCenterPage = lazy(() => import('./pages/HelpCenter'));
const APIPage = lazy(() => import('./pages/API'));
const ContactPage = lazy(() => import('./pages/Contact'));
const Contact2Page = lazy(() => import('./pages/Contact2'));
const PrivacyPage = lazy(() => import('./pages/Privacy'));
const LegalDisclosurePage = lazy(() => import('./pages/LegalDisclosure'));
const TermsPage = lazy(() => import('./pages/Terms'));
const CustomizeWorkflowPage = lazy(() => import('./pages/CustomizeWorkflow'));
const ComplianceAI = lazy(() => import('./pages/ComplianceAI'));
const ComplianceCalendar = lazy(() => import('./pages/ComplianceCalendar'));
const HorizonScanning = lazy(() => import('./pages/HorizonScanning'));
const ToolsPage = lazy(() => import('./pages/Tools'));
const PlatformPage = lazy(() => import('./pages/Platform'));
const AIFMDPage = lazy(() => import('./pages/AIFMD'));
const CSRDPage = lazy(() => import('./pages/CSRD'));
const NIS2Page = lazy(() => import('./pages/NIS2'));
const CTAFormPage = lazy(() => import('./pages/l/ctaform'));
const SmallCTAPage = lazy(() => import('./pages/l/smallcta'));
const AIFMDLanding20 = lazy(() => import('./pages/l/20/aifmd'));
const AIFMDLanding22 = lazy(() => import('./pages/l/22/aifmd'));
const FinanceMarketingLanding = lazy(() => import('./pages/l/23/finance-marketing'));
const AIFMDLanding23 = lazy(() => import('./pages/l/23/aifmd'));
const LuthorAILanding = lazy(() => import('./pages/l/24/luthor-ai'));
const FinanceMarketingWilsonLanding = lazy(() => import('./pages/l/24/finance-marketing-wilson'));
const VercLanding = lazy(() => import('./pages/l/24/verc'));
const TeamworkInspiredLanding = lazy(() => import('./pages/l/teamwork-inspired'));
const TMNGLanding = lazy(() => import('./pages/l/tmng'));
const MinimalLanding = lazy(() => import('./pages/l/minimal'));
const AIFMDMarketingLanding = lazy(() => import('./pages/l/AIFMDMarketing'));
const LandingDirectoryPage = lazy(() => import('./pages/landing'));
const NewDesign = lazy(() => import('./pages/NewDesign'));
const Brand = lazy(() => import('./pages/Brand'));
const WorkflowVisualizer = lazy(() => import('./pages/WorkflowVisualizer'));
import HotjarScript from './components/HotjarScript';
import GoogleAnalytics from './components/GoogleAnalytics';
import LinkedInTrackingScript from './components/LinkedInTrackingScript';
import { trackPageViewEnhanced } from '@/utils/analytics';
import metadataJson from '@/data/metatags.json';
import { CookieBanner } from './components/CookieBanner';
import { useCookieConsent } from './hooks/useCookieConsent';

// Type for metadata
interface MetadataItem {
  title: string;
  description: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  ogUrl: string;
  ogType: string;
  twitterCard: string;
  canonical: string;
  keywords: string;
}

interface MetadataCollection {
  [path: string]: MetadataItem;
}

// Type assertion for imported JSON
const metadata = metadataJson as MetadataCollection;

const queryClient = new QueryClient();

const ScrollToTop = () => {
  const { pathname, search } = useLocation();
  const { t } = useTranslation('common');
  const { consent } = useCookieConsent();

  useEffect(() => {
    // Scroll to top when route changes
    window.scrollTo(0, 0);

    // Get page title from metadata if available
    const metadataPath = metadata[pathname] ? pathname : '/';
    const meta = metadata[metadataPath];

    // Translation keys based on path
    const translationNamespace = metadataPath === '/' ? 'homepage' : metadataPath.replace('/', ''); // Remove leading slash

    // Get page title from translations or metadata
    const pageTitle =
      t(`${translationNamespace}.helmet.title`, { defaultValue: meta?.title }) || document.title;

    // Only track page views if user has consented to analytics
    if (consent?.analytics) {
      trackPageViewEnhanced(pathname, pageTitle, { search });
    }

    // For debugging
    if (import.meta.env.DEV) {
      console.log('Route changed:', {
        path: pathname,
        title: pageTitle,
        search,
      });
    }
  }, [pathname, search, t, consent]);

  return null;
};

const App = () => {
  const isDevelopment = import.meta.env.DEV;
  const { consent } = useCookieConsent();

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <HelmetProvider>
          <Toaster />
          <Sonner />
          {/*{isDevelopment && <DebugPanel/>}*/}
          {consent?.analytics && (
            <>
              <HotjarScript hjid={3876765} hjsv={6} />
              <GoogleAnalytics measurementId="G-ZL8PE90PQ4" />
            </>
          )}
          {consent?.marketing && <LinkedInTrackingScript partnerId="8216449" />}
          <BrowserRouter>
            <ScrollToTop />
            <CookieBanner />
            <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<NewDesign />} />
              <Route path="/features" element={<FeaturesPage />} />
              <Route path="/about" element={<AboutPage />} />
              {/* Sign-up retired — Verdea is demo-led; route any /signup links to Book a Demo */}
              <Route path="/signup" element={<Navigate replace to="/book-demo" />} />
              <Route path="/book-demo" element={<BookDemoPage />} />
              <Route path="/press" element={<PressPage />} />
              <Route path="/learn-more" element={<LearnMorePage />} />
              <Route path="/security" element={<SecurityPage />} />
              <Route path="/enterprise" element={<EnterprisePage />} />
              <Route path="/documentation" element={<DocumentationPage />} />
              <Route path="/help" element={<HelpCenterPage />} />
              <Route path="/api" element={<APIPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/contact2" element={<Contact2Page />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/legal-disclosure" element={<LegalDisclosurePage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/customize" element={<CustomizeWorkflowPage />} />
              <Route path="/visualizer" element={<WorkflowVisualizer />} />
              <Route path="/compliance-ai" element={<ComplianceAI />} />
              <Route path="/compliance-calendar" element={<ComplianceCalendar />} />
              <Route path="/horizon-scanning" element={<HorizonScanning />} />
              <Route path="/tools" element={<ToolsPage />} />
              <Route path="/platform" element={<PlatformPage />} />
              <Route path="/l/ctaform" element={<CTAFormPage />} />
              <Route path="/l/smallcta" element={<SmallCTAPage />} />

              {/* Framework Pages */}
              <Route path="/aifmd" element={<AIFMDPage />} />
              <Route path="/csrd" element={<CSRDPage />} />
              <Route path="/nis2" element={<NIS2Page />} />

              {/* Landing Pages */}
              <Route path="/landing" element={<LandingDirectoryPage />} />
              <Route path="/l/20/aifmd" element={<AIFMDLanding20 />} />
              <Route path="/l/22/aifmd" element={<AIFMDLanding22 />} />
              <Route path="/l/23/aifmd" element={<AIFMDLanding23 />} />
              <Route path="/l/23/finance-marketing" element={<FinanceMarketingLanding />} />
              <Route path="/l/24/luthor-ai" element={<LuthorAILanding />} />
              <Route path="/l/24/fmw" element={<FinanceMarketingWilsonLanding />} />
              <Route path="/l/24/verc" element={<VercLanding />} />
              <Route path="/l/teamwork-inspired" element={<TeamworkInspiredLanding />} />
              <Route path="/l/tmng" element={<TMNGLanding />} />

              <Route path="/l/minimal" element={<MinimalLanding />} />
              <Route path="/regtechai" element={<MinimalLanding />} />
              <Route path="/how-to-optimize-regulations" element={<MinimalLanding />} />
              <Route path="/l/AIFMDMarketing" element={<AIFMDMarketingLanding />} />

              {/* New design is now the homepage; keep /new-design as a redirect for old review links */}
              <Route path="/new-design" element={<Navigate replace to="/" />} />
              <Route path="/brand" element={<Brand />} />

              {/* Short code redirects (e.g., /ES000N5C) */}
              <Route path="/:code" element={<ShortCodeRedirect />} />

              {/* Catch-all route for 404 - redirects to homepage */}
              <Route path="*" element={<Navigate replace to="/" />} />
            </Routes>
            </Suspense>
          </BrowserRouter>
        </HelmetProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
